import React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import { SetURLSearchParams } from "react-router-dom";
import { IClientList } from "../../data/Clients";
import LinearProgress from "@mui/material/LinearProgress";
import ClientListTableRow from "./ClientListTableRow";

type Order = "asc" | "desc";

export interface IClientListTableProps {
  clients?: IClientList | void;
  isLoading: boolean;
  searchParams: URLSearchParams;
  setSearchParams: SetURLSearchParams;
}

const ClientListTable = ({
  clients,
  isLoading,
  searchParams,
  setSearchParams,
}: IClientListTableProps) => {
  const pageSize = Number(searchParams.get("page_size") ?? 10);
  const page = Math.max(Number(searchParams.get("page")) - 1, 0);
  const ordering = searchParams.get("ordering") || "-modified";
  const order: Order = ordering[0] === "-" ? "desc" : "asc";
  const orderBy: string =
    ordering[0] === "-" ? ordering.substring(1) : ordering;

  const createSortHandler = (property: string) => () => {
    const isAsc = orderBy === property && order === "asc";
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("ordering", isAsc ? `-${property}` : property);
      return params;
    });
  };

  const handlePageChange = (event: any, newPage: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", (newPage + 1).toString());
      return params;
    });
  };

  const handlePageSizeChange = (event: any) => {
    const value = parseInt(event.target.value, 10);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page_size", value.toString());
      params.set("page", "1");
      return params;
    });
  };

  const emptyRows = pageSize - (clients?.results?.length ?? 0);

  return (
    <>
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell
                width="30%"
                align="left"
                sortDirection={orderBy === "name" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={createSortHandler("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell width="60%" align="left">
                Sectors
              </TableCell>
              <TableCell width="10%" key="actions" align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {clients?.results.map((client) => (
              <ClientListTableRow key={client.id} client={client} />
            ))}
            {emptyRows > 0 && (
              <TableRow style={{ height: 59 * emptyRows }}>
                <TableCell colSpan={3} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isLoading ? <LinearProgress sx={{ mt: -0.5 }} /> : null}
      <TablePagination
        rowsPerPageOptions={[5, 10, 15, 20]}
        component="div"
        count={clients?.count || -1}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
      />
    </>
  );
};

export default ClientListTable;
